import type { IkeProviderCustom } from '@ikegps/core-api';
import { IkeCloneable, IkeCollectorType, IkeFieldType, IkeProviderType } from '@ikegps/core-api';
import { IkeArray } from '../../util/array';
import type { FormModel } from './form';
import type { FormModelState } from './form.state';
import type { FormModelFieldSubForm } from './subform/form.subform';
import { LOCKED_MODEL_POLE_FIELDS } from './form.field.lock';

export class FormModelMutatorField<T extends FormModelField = FormModelField> {
	field: T;
	constructor(field: T) {
		this.field = field;
	}

	get state(): FormModelState {
		return this.field.getForm().state;
	}

	displayName(displayName: string) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			displayName,
		});
	}

	title(title: boolean) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			title,
		});
		// Unset other titles if this is the new title
		// Unset the subtitle if this is the new title
		if (title === true) {
			for (const field of this.field.parent?.fields ?? []) {
				if (field.title && field.id !== this.field.id) {
					field.Mutator.title(false);
				}
				if (field.subtitle && field.id === this.field.id) {
					field.Mutator.subtitle(false);
				}
			}
		}
	}

	subtitle(subtitle: boolean) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subtitle,
		});
		// Unset other subtitles if this is the new subtitle
		// Unset the title if this is the new subtitle
		if (subtitle === true) {
			for (const field of this.field.parent?.fields ?? []) {
				if (field.subtitle && field.id !== this.field.id) {
					field.Mutator.subtitle(false);
				}
				if (field.title && field.id === this.field.id) {
					field.Mutator.title(false);
				}
			}
		}
	}

	isStatusField(useAsStatus: boolean) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			isStatusField: useAsStatus,
		});

		if (useAsStatus) {
			this.field.getForm().metadata.setStatusField(this.field);

			this.field.parent?.fields.forEach((field) => {
				if (field.isStatusField && field.id !== this.field.id) {
					field.Mutator.isStatusField(false);
				}
			});
		} else {
			this.field.getForm().metadata.removeStatusField(this.field);
		}
	}

	isMapColorField(useAsMapColorField: boolean) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			isMapColorField: useAsMapColorField,
		});

		if (useAsMapColorField) {
			this.field.getForm().metadata.setMapColorField(this.field);

			this.field.parent?.fields.forEach((field) => {
				if (field.isMapColorField && field.id !== this.field.id) {
					field.Mutator.isMapColorField(false);
				}
			});
		} else {
			this.field.getForm().metadata.removeMapColorField(this.field);
		}
	}

	annotationLabelFieldId(annotationLabelFieldId: string) {
		if (this.field.type === IkeFieldType.HEIGHT || this.field.type === IkeFieldType.VERTICAL_SEPARATION) {
			this.state.setFieldValue(this.field.fieldId, {
				...this.field.stateValue,
				annotationLabelFieldId,
			});
		}
	}

	clearanceThresholdInMeters(clearanceThresholdInMeters) {
		if (this.field.type === IkeFieldType.VERTICAL_SEPARATION) {
			this.state.setFieldValue(this.field.fieldId, {
				...this.field.stateValue,
				clearanceThresholdInMeters,
			});
		}
	}

	required(required: boolean) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			required,
		});
	}

	isAddressLookupField(isAddress: boolean) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			isAddressLookupField: isAddress,
		});

		if (isAddress) {
			this.field.getForm().metadata.setAddressLookupField(this.field);

			this.field.getForm().getFieldsRecursive.forEach((field) => {
				if (field.isAddressLookupField && field.fieldId !== this.field.fieldId) {
					field.Mutator.isAddressLookupField(false);
				}
			});
		} else {
			this.field.getForm().metadata.removeAddressLookupField(this.field);
		}
	}

	adminOnly(adminOnly: boolean) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			adminOnly,
		});
		//Set field as hidden if the field is admin-only
		if (adminOnly) {
			this.field.Mutator.collector(IkeCollectorType.OFFICE);
		}
	}

	collector(collector: IkeCollectorType) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			collector,
		});
	}

	cloneable(cloneable: IkeCloneable) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			cloneable,
		});
	}

	/** Overwrite the list of providers with this list */
	providers(providers: IkeProviderCustom[]) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			providers,
		});
	}

	/** Toggle a specific provider on or off */
	provider(providerType: IkeProviderType) {
		const stateValue = this.field.stateValue;
		const providers = IkeArray.clone(stateValue.providers ?? null);
		const foundIndex = providers.findIndex((f) => f.type === providerType);

		if (foundIndex == null) {
			providers.push({ type: providerType });
		} else {
			providers.splice(foundIndex, 1);
		}
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			providers,
		});
	}

	/** Move the field up one position in the form */
	moveUp() {
		this.field.parent?.Mutator.move(this.field.id, -1);
	}

	/** Move the field up one position in the form */
	moveDown() {
		this.field.parent?.Mutator.move(this.field.id, 1);
	}

	delete() {
		this.field.parent?.Mutator.remove(this.field.id);
	}

	setLockState(lockState: boolean) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			locked: lockState,
		});
	}

	isGenerated(isGenerated: boolean) {
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			isGenerated: isGenerated,
		});
	}
}

export interface FormModelFieldState {
	id: string;
	displayName: string;
	title?: boolean;
	subtitle?: boolean;
	adminOnly?: boolean;
	isStatusField?: boolean;
	isMapColorField?: boolean;
	required?: boolean;
	isAddressLookupField?: boolean;
	collector?: IkeCollectorType;
	cloneable?: IkeCloneable;
	providers?: IkeProviderCustom[];
	type: IkeFieldType;
	annotationLabelFieldId?: string;
	clearanceThresholdInMeters?: number;
	locked?: boolean;
	isGenerated?: boolean;
}

/**
 * Returns true if the given state property is allowed on fields of `type`.
 *
 * Mirrors the per-type constraints enforced by the form designer UI. Used by the
 * subform mutator when replacing a field's type to strip flags that no longer apply
 * (e.g. dropping `isAddressLookupField` when switching from TEXT → IMAGE).
 */
export function isFieldStateKeyValidForType(key: keyof FormModelFieldState, type: IkeFieldType): boolean {
	switch (key) {
		case 'id':
		case 'type':
		case 'displayName':
		case 'adminOnly':
		case 'collector':
		case 'locked':
		case 'isGenerated':
			return true;
		case 'title':
		case 'subtitle':
			return (
				type === IkeFieldType.TEXT ||
				type === IkeFieldType.NUMBER ||
				type === IkeFieldType.SELECT_LIST ||
				type === IkeFieldType.NESTED_LIST
			);
		case 'isStatusField':
		case 'isMapColorField':
			return type === IkeFieldType.NESTED_LIST || type === IkeFieldType.SELECT_LIST;
		case 'isAddressLookupField':
			return type === IkeFieldType.TEXT;
		case 'required':
			return (
				type !== IkeFieldType.FORM &&
				type !== IkeFieldType.VERTICAL_SEPARATION &&
				type !== IkeFieldType.YES_NO &&
				type !== IkeFieldType.FILE_TOOL
			);
		case 'cloneable':
			return (
				type === IkeFieldType.DATE ||
				type === IkeFieldType.FORM ||
				type === IkeFieldType.NESTED_LIST ||
				type === IkeFieldType.NUMBER ||
				type === IkeFieldType.ORIENTATION ||
				type === IkeFieldType.SELECT_LIST ||
				type === IkeFieldType.TEXT ||
				type === IkeFieldType.YES_NO
			);
		case 'annotationLabelFieldId':
			return type === IkeFieldType.HEIGHT || type === IkeFieldType.VERTICAL_SEPARATION;
		case 'clearanceThresholdInMeters':
			return type === IkeFieldType.VERTICAL_SEPARATION;
		case 'providers':
			// Providers carry type-specific value shapes; clear on any type change.
			return false;
		default:
			return true;
	}
}

/**
 * Build a new field state for `type`, preserving from `state` only the properties that are
 * valid for `type`. Type-specific extended state (subform fields/label, select/nested list
 * options) is also dropped — those keys aren't part of FormModelFieldState and don't survive
 * a type change.
 */
export function pruneFieldStateForType(state: FormModelFieldState, type: IkeFieldType): FormModelFieldState {
	const result: FormModelFieldState = {
		id: state.id,
		type,
		displayName: state.displayName,
	};
	const knownKeys: (keyof FormModelFieldState)[] = [
		'title',
		'subtitle',
		'adminOnly',
		'isStatusField',
		'isMapColorField',
		'required',
		'isAddressLookupField',
		'collector',
		'cloneable',
		'providers',
		'annotationLabelFieldId',
		'clearanceThresholdInMeters',
		'locked',
		'isGenerated',
	];

	for (const key of knownKeys) {
		if (state[key] !== undefined && isFieldStateKeyValidForType(key, type)) {
			assignExistingStateKey(result, key, state[key]);
		}
	}
	return result;
}

/**
 * Helper function for type safety
 */
function assignExistingStateKey<K extends keyof FormModelFieldState>(
	obj: FormModelFieldState,
	key: K,
	value: FormModelFieldState[K]
) {
	obj[key] = value;
}

export class FormModelField<T extends FormModelFieldState = FormModelFieldState> {
	protected _id: string;

	public get id(): string {
		return this._id;
	}
	public set id(value: string) {
		this._id = value;
	}

	parent?: FormModelFieldSubForm;

	get fieldId() {
		if (this.parent == null) {
			return ':' + this.id;
		}
		return this.parent.id + ':' + this.id;
	}

	private _Mutator: FormModelMutatorField | null = null;
	get Mutator(): FormModelMutatorField {
		if (this._Mutator == null) {
			this._Mutator = new FormModelMutatorField(this);
		}
		return this._Mutator;
	}

	constructor(parent: FormModelFieldSubForm | null, id: string | null) {
		this._id = id;
		this.parent = parent ?? undefined;

		const { metadata } = this.getForm();

		if (metadata && metadata.statusFieldId === id) {
			this.stateValue.isStatusField = true;
		}

		if (metadata && metadata.mapColorFieldId === id) {
			this.stateValue.isMapColorField = true;
		}

		if (metadata && metadata.addressLookupFieldId === id) {
			this.stateValue.isAddressLookupField = true;
		}
	}

	getForm(): FormModel {
		return this.parent!.getForm();
	}

	get displayName() {
		return this.stateValue.displayName;
	}

	get title() {
		return this.stateValue.title;
	}

	get subtitle() {
		return this.stateValue.subtitle;
	}

	get isStatusField() {
		return this.stateValue.isStatusField;
	}

	get isMapColorField() {
		return this.stateValue.isMapColorField;
	}

	get required() {
		return this.stateValue.required;
	}
	get isAddressLookupField() {
		return this.stateValue.isAddressLookupField;
	}

	get adminOnly() {
		return this.stateValue.adminOnly;
	}

	get collector() {
		return this.stateValue.collector;
	}

	get annotationLabelFieldId() {
		return this.stateValue.annotationLabelFieldId;
	}

	get clearanceThresholdInMeters() {
		return this.stateValue.clearanceThresholdInMeters;
	}

	get cloneable() {
		return this.stateValue.cloneable;
	}

	get providers() {
		return this.stateValue.providers;
	}

	get type(): IkeFieldType {
		return this.stateValue.type;
	}

	get isCloneable() {
		const cloneable = this.stateValue.cloneable;
		return cloneable != null && cloneable !== IkeCloneable.NONE;
	}

	get isRoot() {
		return this.parent?.parent == null;
	}

	get stateValue(): T {
		return this.getForm().state.getFieldValue(this.fieldId) as T;
	}

	get isLocked(): boolean {
		const lockedState = this.stateValue?.locked;
		// If we have locked specifically set to false (instead of undefined), it should override the legacy hard coded fields
		if (lockedState === undefined) {
			return Boolean(LOCKED_MODEL_POLE_FIELDS[this.id]);
		}

		return lockedState;
	}

	get isGenerated(): boolean {
		return this.stateValue?.isGenerated || false;
	}
}
